import { useState, useEffect } from 'react';
import { Flex, Box, Heading, Text, Steps, Button, IconButton, Icon } from '@vibe/core';
import { Modal } from '@vibe/core';
import { ExternalLink } from './ExternalLink';

type OnboardingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialStep?: number | null;
};

export function OnboardingModal({ isOpen, onClose, initialStep = null }: OnboardingModalProps) {
  const steps = [
    {
      fade: true,
      video: '/Onboarding-Login-Method-Compressed.mp4',
      poster: '/Onboarding-Login-Method-Poster.webp',
      title: 'Give clients access to the portal',
      text: (
        <>
          Open a client item on your{' '}
          <ExternalLink href="https://getgorilla.app/products/client-portal/docs/getting-started/clients-board" tabIndex={-1}>
            Clients Board
          </ExternalLink>{' '}
          and add the{' '}
          <ExternalLink
            href="https://getgorilla.app/products/client-portal/docs/getting-started/app-views/client-settings-view"
            tabIndex={-1}
          >
            Client Settings
          </ExternalLink>{' '}
          view. That's where you set up{' '}
          <ExternalLink href="https://getgorilla.app/products/client-portal/docs/user-guides/client-access" tabIndex={-1}>
            how a client signs in to the portal
          </ExternalLink>
          , and where you grant or revoke their general portal access.
        </>
      ),
    },
    {
      fade: true,
      image: '/onboarding-docs.webp',
      title: 'Documentation and user guides',
      text: (
        <>
          We recommend to check out our{' '}
          <ExternalLink href="https://getgorilla.app/products/client-portal/docs" tabIndex={-1}>
            documentation
          </ExternalLink>{' '}
          and{' '}
          <ExternalLink href="https://getgorilla.app/products/client-portal/docs/user-guides" tabIndex={-1}>
            user guides
          </ExternalLink>{' '}
          for detailed instructions on setting up and using the <strong>Client Portal Builder</strong>, as well as best practices and
          advanced tips.
        </>
      ),
    },
    {
      fade: true,
      image: '/onboarding-first-steps.webp',
      title: 'Getting started and first steps',
      text: (
        <>
          To get started right away, check out our{' '}
          <ExternalLink href="https://www.youtube.com/watch?v=Pf0J6atpi50" tabIndex={-1}>
            first steps video guide
          </ExternalLink>{' '}
          and our{' '}
          <ExternalLink href="https://getgorilla.app/products/client-portal/docs/getting-started#anchor-6983" tabIndex={-1}>
            getting started documentation
          </ExternalLink>
          . They will walk you through the process of setting up a basic client portal and sharing it with your clients.
        </>
      ),
    },
  ];

  const stepCount = steps.length;
  const clamp = (n: number) => Math.min(Math.max(n, 0), stepCount - 1);

  const effectiveInitialStep = clamp(initialStep ?? 0);

  const [activeStep, setActiveStep] = useState(() => effectiveInitialStep);

  useEffect(() => {
    setActiveStep(effectiveInitialStep);
  }, [effectiveInitialStep]);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === stepCount - 1;

  const next = () => setActiveStep((s) => clamp(s + 1));
  const back = () => setActiveStep((s) => clamp(s - 1));

  const primaryButtonText = isLastStep ? 'Got it' : 'Next';
  const secondaryButtonText = isFirstStep ? 'Dismiss' : 'Back';

  const { image, video, poster, fade, title, text } = steps[activeStep];

  const isSingleStepMode = initialStep != null;

  return (
    <Modal id="onboardingModal" size="medium" closeButtonTheme="light" alertModal={!isSingleStepMode} show={isOpen} onClose={onClose}>
      <div
        style={{
          overflow: 'hidden',
          overflowY: 'auto',
        }}
        className="onboardingModal__content"
      >
        <Flex direction="column">
          <Box
            style={{
              aspectRatio: '1181/520', // 540
              overflow: 'visible',
              position: 'relative',
              width: '100%',
              backgroundColor: '#edf1fc',
              lineHeight: 0,
            }}
          >
            {image && (
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  margin: '10px 10px 0 10px',
                }}
              >
                <img
                  src={image}
                  alt=""
                  style={{
                    pointerEvents: 'none',
                    width: '100%',
                    borderTopRightRadius: '7px',
                    borderTopLeftRadius: '7px',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                  }}
                />
              </div>
            )}
            {video && (
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  margin: '10px 10px 0 10px',
                }}
              >
                {poster && (
                  <img
                    src={poster}
                    alt=""
                    style={{
                      pointerEvents: 'none',
                      width: '100%',
                      borderTopRightRadius: '7px',
                      borderTopLeftRadius: '7px',
                      objectFit: 'cover',
                      objectPosition: 'center top',
                    }}
                  />
                )}
                <video
                  src={video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    pointerEvents: 'none',
                    aspectRatio: '1181/540',
                    overflow: 'hidden',
                    margin: '-2px 0 0 0',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 'calc(100% + 2px)',
                    height: '100%',
                    borderTopRightRadius: '10px',
                    borderTopLeftRadius: '10px',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                  }}
                />
              </div>
            )}
            {fade && (
              <div
                style={{
                  zIndex: 2,
                  position: 'absolute',
                  right: 0,
                  bottom: '8px',
                  left: 0,
                  width: '100%',
                  height: '30px',
                  background: 'linear-gradient(180deg, rgba(237, 241, 252, 0) 0%, rgba(237, 241, 252, 1) 100%)',
                }}
              ></div>
            )}
            <div
              style={{
                zIndex: 3,
                position: 'absolute',
                right: 0,
                bottom: '-1px',
                left: 0,
                width: '100%',
                height: '10px',
                backgroundColor: '#edf1fc',
              }}
            ></div>
          </Box>

          <Flex
            direction="column"
            gap={16}
            style={{
              padding: !isSingleStepMode ? '40px 48px 24px 48px' : '40px 48px 48px 48px',
            }}
          >
            <Heading type="h2" weight="medium" align="center" element="h2" style={{ color: '#0073ea' }}>
              {title}
            </Heading>
            <Text
              type="text2"
              weight="normal"
              align="center"
              element="p"
              color="secondary"
              style={{
                maxWidth: '100%',
              }}
            >
              {text}
            </Text>
          </Flex>
        </Flex>

        {!isSingleStepMode && (
          <Box paddingEnd="large" paddingBottom="large" paddingStart="large">
            <Flex gap={16} justify="center" align="center" style={{ width: '100%' }}>
              <div style={{ width: '200px' }}>
                <Button
                  kind="tertiary"
                  size="medium"
                  onClick={isFirstStep ? onClose : back}
                  //disabled={isFirstStep}
                  tabIndex={-1}
                >
                  {secondaryButtonText}
                </Button>
              </div>

              <div style={{ pointerEvents: 'none', flex: 1, position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, calc(-50% + 5px))',
                  }}
                >
                  <Steps
                    id="onboardingModalSteps"
                    steps={steps.map((_, i) => (
                      <span key={i} tabIndex={-1} />
                    ))}
                    activeStepIndex={activeStep}
                    areNavigationButtonsHidden
                  />
                </div>
              </div>

              <div style={{ width: '200px', textAlign: 'right' }}>
                <Button kind="primary" size="medium" onClick={isLastStep ? onClose : next} tabIndex={-1}>
                  {primaryButtonText}
                </Button>
              </div>
            </Flex>
          </Box>
        )}
      </div>
    </Modal>
  );
}
